import { useEffect, useLayoutEffect } from '../lib/teact/teact';
import { getActions, withGlobal } from '../global';

import type { GlobalState } from '../global/types';
import type { ThemeKey } from '../types';
import type { UiLoaderPage } from './common/UiLoader';
import { MAIN_THREAD_ID } from '../api/types';

import {
  DARK_THEME_BG_COLOR,
  INACTIVE_MARKER,
  LIGHT_THEME_BG_COLOR,
  PAGE_TITLE,
  PAGE_TITLE_TAURI,
  SESSION_ACCOUNT_PREFIX,
} from '../config';
import { forceMutation } from '../lib/fasterdom/stricterdom.ts';
import {
  selectActionMessageBg,
  selectChat,
  selectChatByUsername,
  selectChatMessages,
  selectDraft,
  selectTabState,
  selectTheme,
  selectUser,
} from '../global/selectors';
import { IS_TAURI } from '../util/browser/globalEnvironment';
import { IS_INSTALL_PROMPT_SUPPORTED, PLATFORM_ENV } from '../util/browser/windowEnvironment';
import { getWindowLocalStorage } from '../util/browserStorage';
import buildClassName from '../util/buildClassName';
import {
  buildWorkGoogleBridgeSaasReturnUrl,
  isWorkGoogleBridgeCallback,
  readCanonicalWorkGoogleBridgeTransactionTruth,
  readWorkGoogleBridgeCallbackTransactionId,
  shouldPreferPhoneAuth,
} from '../util/googleAuth';
import { setupBeforeInstallPrompt } from '../util/installPrompt';
import { ACCOUNT_SLOT, getAccountsInfo, getAccountSlotUrl, loadSlotSession } from '../util/multiaccount';
import { hasEncryptedSession } from '../util/passcode';
import { buildPublicAppReturnUrl } from '../util/publicAppReturn';
import { getInitialLocationHash, parseInitialLocationHash } from '../util/routing';
import {
  buildLegacySaasHandoffText,
  buildSaasHandoffBootstrap,
  clearSaasHandoffSearchParams,
} from '../util/saasHandoffBootstrap';
import { buildSaasHandoffResumeRequest } from '../util/saasHandoffBootstrap';
import {
  buildStorageSessionRequestBody,
  clearPendingSaasHandoffBootstrap,
  clearPendingSaasHandoffIntent,
  getCompatiblePendingSaasHandoffBootstrap,
} from '../util/saasHandoffIntent';
import { attemptResolvedSaasHandoffOpen } from '../util/saasHandoffResolver';
import { rememberSaasMetaCapiTarget } from '../util/saasMetaCapi';
import { checkSessionLocked, hasStoredSession } from '../util/sessions';
import { updateSizes } from '../util/windowSize';

import useTauriDrag from '../hooks/tauri/useTauriDrag';
import useAppLayout from '../hooks/useAppLayout';
import usePrevious from '../hooks/usePrevious';
import { useSignalEffect } from '../hooks/useSignalEffect';
import { getIsInBackground } from '../hooks/window/useBackgroundMode';

// import Test from './test/TestSvg';
import Auth from './auth/Auth';
import Notifications from './common/Notifications';
import TermsModal from './common/TermsModal';
import UiLoader from './common/UiLoader';
import AppInactive from './main/AppInactive';
import LockScreen from './main/LockScreen.async';
import Main from './main/Main.async';
import Transition from './ui/Transition';

import styles from './App.module.scss';

type StateProps = {
  authState: GlobalState['auth']['state'];
  connectionState?: GlobalState['connectionState'];
  currentUserId?: string;
  isScreenLocked?: boolean;
  hasPasscode?: boolean;
  inactiveReason?: 'auth' | 'otherClient';
  hasWebAuthTokenFailed?: boolean;
  isTestServer?: boolean;
  theme: ThemeKey;
  actionMessageBg?: string;
  isRunTermsNeeded: boolean;
};

enum AppScreens {
  auth,
  main,
  lock,
  inactive,
}

const TRANSITION_RENDER_COUNT = Object.keys(AppScreens).length / 2;
const ACTIVE_PAGE_TITLE = IS_TAURI ? PAGE_TITLE_TAURI : PAGE_TITLE;
const INACTIVE_PAGE_TITLE = `${ACTIVE_PAGE_TITLE} ${INACTIVE_MARKER}`;
const SAAS_HANDOFF_RETRY_DELAY_MS = 100;
const SAAS_HANDOFF_MAX_RETRIES = 20;
const WORK_GOOGLE_BRIDGE_PHONE_MARKER = 'runai.work_google_bridge.phone_selected';
const WORK_GOOGLE_BRIDGE_CALLBACK_MARKER = 'runai.work_google_bridge.callback_transaction';
const WORK_RECENT_SESSION_MARKER = 'runai.work.recent_session';

// Same-origin path validator for ?return_to= on saas sign-in flows.
// The standalone Work shell (run-saas /work) sends users here with
// `?run_source=saas&return_to=<path>` so we can drop them back on their
// original deep link after sign-in. Rejects anything that could become
// cross-origin (`//host`, `/\host`) — same-origin paths only.
function readSafeSaasReturnTo(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search);
    const bootstrapHint = getCompatiblePendingSaasHandoffBootstrap(
      window.location.search,
    );
    if (params.get('run_source') !== 'saas' && bootstrapHint?.source !== 'saas') return undefined;
    const raw = params.get('return_to')?.trim() || bootstrapHint?.returnTo?.trim();
    if (!raw) return undefined;
    if (!raw.startsWith('/')) return undefined;
    if (raw.startsWith('//') || raw.startsWith('/\\')) return undefined;
    return raw;
  } catch {
    return undefined;
  }
}

function readSafeWorkAccountBridgeReturnTo(): string | undefined {
  try {
    const normalize = (raw?: string) => {
      const value = raw?.trim();
      if (!value) return undefined;
      if (!value.startsWith('/')) return undefined;
      if (value.startsWith('//') || value.startsWith('/\\')) return undefined;
      return value;
    };
    const params = new URLSearchParams(window.location.search);
    if (params.get('run_source') === 'work_account_bridge') {
      const direct = normalize(params.get('return_to') || undefined);
      if (direct) return direct;
    }
    const bootstrapHint = getCompatiblePendingSaasHandoffBootstrap(window.location.search);
    if (bootstrapHint?.source === 'work_account_bridge') {
      return normalize(bootstrapHint.returnTo);
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function isSaasWorkReturnPath(path: string): boolean {
  try {
    const pathname = new URL(path, window.location.origin).pathname;
    return pathname === '/work' || pathname.startsWith('/work/');
  } catch {
    return false;
  }
}

function readSaasOwnerAccessToken(): string | undefined {
  try {
    const bootstrapHint = getCompatiblePendingSaasHandoffBootstrap(
      window.location.search,
    );
    return (
      new URLSearchParams(window.location.search).get('ownerAccessToken')?.trim()
        || bootstrapHint?.ownerAccessToken?.trim()
        || undefined
    );
  } catch {
    return undefined;
  }
}

function readSaasTargetHost(): string | undefined {
  const normalize = (raw?: string) => {
    const value = raw?.trim().toLowerCase();
    if (!value) return undefined;
    try {
      const parsed = new URL(`https://${value}`);
      if (parsed.username || parsed.password || parsed.port) return undefined;
      if (parsed.pathname !== '/' || parsed.search || parsed.hash) return undefined;
      const labels = parsed.hostname.split('.');
      const suffix = labels.slice(-3).join('.');
      const tenantLabel = labels.length === 4 ? labels[0] : '';
      if (!tenantLabel || suffix !== 'i.run.ceo') return undefined;
      return parsed.hostname;
    } catch {
      return undefined;
    }
  };
  try {
    const direct = normalize(new URLSearchParams(window.location.search).get('run_target_host') || undefined);
    if (direct) return direct;
  } catch {
    // no-op
  }
  return normalize(getCompatiblePendingSaasHandoffBootstrap(window.location.search)?.targetHost);
}

function resolveEffectiveSaasWorkTargetHost(
  targetHost: string | undefined,
  returnTo: string | undefined,
): string | undefined {
  if (targetHost) {
    return targetHost;
  }
  if (returnTo && isSaasWorkReturnPath(returnTo)) {
    const labels = window.location.hostname.toLowerCase().split('.');
    if (labels.length === 4 && labels.slice(-3).join('.') === 'i.run.ceo' && labels[0]) {
      return window.location.hostname.toLowerCase();
    }
  }
  return undefined;
}

function readTenantIdFromSaasTargetHost(targetHost?: string): string | undefined {
  const labels = targetHost?.toLowerCase().split('.') || [];
  if (labels.length !== 4 || labels.slice(-3).join('.') !== 'i.run.ceo') return undefined;
  return labels[0] || undefined;
}

function readSaasTargetTenantId(): string | undefined {
  const targetHost = readSaasTargetHost();
  const targetTenantId = readTenantIdFromSaasTargetHost(targetHost);
  if (!targetTenantId) return undefined;

  const normalizeExplicitTenant = (raw?: string) => {
    const value = raw?.trim().toLowerCase();
    if (!value) return undefined;
    return value;
  };

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const directTenantId = normalizeExplicitTenant(searchParams.get('run_target_tenant') || undefined);
    if (directTenantId) return directTenantId === targetTenantId ? directTenantId : undefined;
  } catch {
    // no-op
  }

  const bootstrapHint = getCompatiblePendingSaasHandoffBootstrap(window.location.search);
  const bootstrapTenantId = normalizeExplicitTenant(bootstrapHint?.targetTenantId);
  if (bootstrapTenantId) return bootstrapTenantId === targetTenantId ? bootstrapTenantId : undefined;
  return targetTenantId;
}

function readPendingWorkGoogleBridgePhoneSelectionTransactionId(): string | undefined {
  try {
    const raw = window.sessionStorage.getItem(WORK_GOOGLE_BRIDGE_PHONE_MARKER)?.trim();
    return raw || undefined;
  } catch {
    return undefined;
  }
}

function readPendingWorkGoogleBridgeCallbackTransactionId(): string | undefined {
  try {
    const raw = window.sessionStorage.getItem(WORK_GOOGLE_BRIDGE_CALLBACK_MARKER)?.trim();
    return raw || undefined;
  } catch {
    return undefined;
  }
}

function rememberPendingWorkGoogleBridgeCallbackTransactionId(transactionId: string) {
  try {
    if (transactionId.trim()) {
      window.sessionStorage.setItem(WORK_GOOGLE_BRIDGE_CALLBACK_MARKER, transactionId.trim());
    }
  } catch {
    // ignore storage failures
  }
}

function clearPendingWorkGoogleBridgePhoneSelection() {
  try {
    window.sessionStorage.removeItem(WORK_GOOGLE_BRIDGE_PHONE_MARKER);
  } catch {
    // ignore storage failures
  }
}

function clearPendingWorkGoogleBridgeCallbackTransaction() {
  try {
    window.sessionStorage.removeItem(WORK_GOOGLE_BRIDGE_CALLBACK_MARKER);
  } catch {
    // ignore storage failures
  }
}

function rememberRecentWorkSessionMarker() {
  try {
    window.sessionStorage.setItem(WORK_RECENT_SESSION_MARKER, '1');
  } catch {
    // ignore storage failures
  }
}

async function fetchSaasOwnerAccessToken(): Promise<string | undefined> {
  try {
    const response = await fetch('/api/run/saas-bootstrap', {
      credentials: 'same-origin',
    });
    if (!response.ok) {
      return undefined;
    }
    const payload = await response.json().catch(() => undefined) as { ownerAccessToken?: string } | undefined;
    return payload?.ownerAccessToken?.trim() || undefined;
  } catch {
    return undefined;
  }
}

type WorkAccountBridgeEntry = {
  slot: number;
  userId: string;
  tenantId: string;
  displayName?: string;
  ownerAccessToken: string;
  expiresAt?: number;
};

function buildWorkAccountBridgeAuthProof(slot: number) {
  const sessionData = loadSlotSession(slot === 1 ? undefined : slot);
  const mainDcId = sessionData?.dcId;
  const authKeyHex = mainDcId ? sessionData?.[`dc${mainDcId}_auth_key` as keyof typeof sessionData] : undefined;
  if (
    !mainDcId
    || typeof authKeyHex !== 'string'
    || !/^[0-9a-f]+$/i.test(authKeyHex)
    || authKeyHex.length % 2 !== 0
  ) {
    return undefined;
  }
  return {
    mainDcId,
    authKeyHex,
    ...(sessionData?.isTest ? { isTest: true as const } : {}),
  };
}

function displayNameForWorkAccountBridgeSlot(slot: number): string | undefined {
  const info = getAccountsInfo()[slot];
  const pieces = [info?.firstName, info?.lastName]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);
  return pieces.join(' ') || info?.phone || info?.userId;
}

function readStoredWorkAccountBridgeSlots(): number[] {
  try {
    const storage = getWindowLocalStorage();
    if (!storage) return [];
    return Object.keys(storage)
      .filter((key) => key.startsWith(SESSION_ACCOUNT_PREFIX))
      .map((key) => Number(key.slice(SESSION_ACCOUNT_PREFIX.length)))
      .filter((slot) => Number.isInteger(slot) && slot > 0);
  } catch {
    return [];
  }
}

function enumerateWorkAccountBridgeSlots(): number[] {
  const slots = new Set<number>();
  Object.keys(getAccountsInfo())
    .map((slot) => Number(slot))
    .filter((slot) => Number.isInteger(slot) && slot > 0)
    .forEach((slot) => slots.add(slot));
  readStoredWorkAccountBridgeSlots().forEach((slot) => slots.add(slot));
  slots.add(ACCOUNT_SLOT || 1);
  return Array.from(slots).sort((a, b) => a - b);
}

async function fetchWorkAccountBridgeEntries(targetTenantId: string): Promise<WorkAccountBridgeEntry[]> {
  const slots = enumerateWorkAccountBridgeSlots();
  const entries: WorkAccountBridgeEntry[] = [];
  for (const slot of slots) {
    const authSessionProof = buildWorkAccountBridgeAuthProof(slot);
    if (!authSessionProof) continue;
    try {
      const response = await fetch('/api/run/work/account-bridge-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ authSessionProof, ...(targetTenantId ? { targetTenantId } : {}) }),
      });
      if (!response.ok) continue;
      const payload = await response.json().catch(() => undefined) as {
        userId?: string;
        tenantId?: string;
        ownerAccessToken?: string;
        expiresAt?: number;
      } | undefined;
      const userId = payload?.userId?.trim();
      const tenantId = payload?.tenantId?.trim();
      const ownerAccessToken = payload?.ownerAccessToken?.trim();
      const displayName = displayNameForWorkAccountBridgeSlot(slot);
      if (!userId || !tenantId || !ownerAccessToken) continue;
      if (targetTenantId && tenantId !== targetTenantId) continue;
      entries.push({
        slot,
        userId,
        tenantId,
        ownerAccessToken,
        ...(typeof payload?.expiresAt === 'number' ? { expiresAt: payload.expiresAt } : {}),
        ...(displayName ? { displayName } : {}),
      });
    } catch {
      // Continue with other local accounts; one stale webchat slot must not
      // block bridging the rest.
    }
  }
  return entries;
}

async function fetchWorkAccountBridgeEntriesWithRetry(targetTenantId: string): Promise<WorkAccountBridgeEntry[]> {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const entries = await fetchWorkAccountBridgeEntries(targetTenantId);
    if (entries.length > 0) return entries;
    await new Promise((resolve) => {
      window.setTimeout(resolve, 150);
    });
  }
  return [];
}

function encodeWorkAccountBridgePayload(entries: WorkAccountBridgeEntry[]): string {
  const json = JSON.stringify({ schema: 'runai.work.accountBridge.v1', accounts: entries, createdAt: Date.now() });
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

async function ensureSaasWorkStorageSession(
  ownerAccessToken?: string,
  options?: { allowGenericAuthSessionProof?: boolean },
): Promise<boolean> {
  for (let attempt = 0; attempt < SAAS_HANDOFF_MAX_RETRIES; attempt += 1) {
    const normalizedOwnerAccessToken = ownerAccessToken?.trim();
    const body = normalizedOwnerAccessToken
      ? { ownerAccessToken: normalizedOwnerAccessToken, source: 'saas' as const }
      : (
        options?.allowGenericAuthSessionProof === false
          ? undefined
          : buildStorageSessionRequestBody(undefined, {
            allowSaasBootstrap: true,
            search: window.location.search,
          })
      );
    if (!body) {
      if (attempt < SAAS_HANDOFF_MAX_RETRIES - 1) {
        await new Promise((resolve) => window.setTimeout(resolve, SAAS_HANDOFF_RETRY_DELAY_MS));
      }
      continue;
    }

    try {
      const response = await fetch('/api/storage-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'same-origin',
      });
      if (response.ok) {
        return true;
      }
    } catch {
      // Ignore and retry below.
    }

    if (attempt < SAAS_HANDOFF_MAX_RETRIES - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, SAAS_HANDOFF_RETRY_DELAY_MS));
    }
  }

  return false;
}

async function waitForWorkGoogleBridgeAuthSessionProof(): Promise<unknown> {
  for (let attempt = 0; attempt < SAAS_HANDOFF_MAX_RETRIES; attempt += 1) {
    const storageSessionBody = buildStorageSessionRequestBody(undefined, {
      allowSaasBootstrap: true,
      search: window.location.search,
    });
    const authSessionProof = (
      storageSessionBody
      && 'authSessionProof' in storageSessionBody
      && storageSessionBody.authSessionProof
    )
      ? storageSessionBody.authSessionProof
      : undefined;
    if (authSessionProof) {
      return authSessionProof;
    }
    if (attempt < SAAS_HANDOFF_MAX_RETRIES - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, SAAS_HANDOFF_RETRY_DELAY_MS));
    }
  }
  return undefined;
}

const App = ({
  authState,
  connectionState,
  currentUserId,
  isScreenLocked,
  hasPasscode,
  inactiveReason,
  hasWebAuthTokenFailed,
  isTestServer,
  theme,
  actionMessageBg,
  isRunTermsNeeded,
}: StateProps) => {
  const {
    signOut,
    checkRunTermsOfService,
    acceptRunTermsOfService,
  } = getActions();
  const { isMobile } = useAppLayout();
  const isMobileOs = PLATFORM_ENV === 'iOS' || PLATFORM_ENV === 'Android';

  useEffect(() => {
    if (authState === 'authorizationStateReady') {
      checkRunTermsOfService();
    }
  }, [authState, checkRunTermsOfService]);

  useEffect(() => {
    const currentBridgeTransactionId = readWorkGoogleBridgeCallbackTransactionId(
      window.location.search,
      window.location.pathname,
      window.location.hostname,
    );
    if (
      currentBridgeTransactionId
      && isWorkGoogleBridgeCallback(window.location.search, window.location.pathname, window.location.hostname)
    ) {
      rememberPendingWorkGoogleBridgeCallbackTransactionId(currentBridgeTransactionId);
    }

    const resumeRequest = buildSaasHandoffResumeRequest({ currentUserId });
    const workBridgeReturnTo = readSafeWorkAccountBridgeReturnTo();
    const saasReturnTo = readSafeSaasReturnTo();
    const ownerAccessToken = readSaasOwnerAccessToken();
    const targetHost = resolveEffectiveSaasWorkTargetHost(readSaasTargetHost(), saasReturnTo);
    const allowSameHostWorkReturn = Boolean(
      saasReturnTo
      && isSaasWorkReturnPath(saasReturnTo)
      && ownerAccessToken
      && targetHost === window.location.host,
    );
    if (authState !== 'authorizationStateReady' && !resumeRequest && !workBridgeReturnTo && !allowSameHostWorkReturn) {
      return;
    }

    const publicAppReturnUrl = currentUserId ? buildPublicAppReturnUrl({ currentUserId }) : undefined;
    if (publicAppReturnUrl) {
      window.location.assign(publicAppReturnUrl);
      return;
    }

    const hasPendingWorkReturn = Boolean(
      (workBridgeReturnTo && isSaasWorkReturnPath(workBridgeReturnTo))
      || (saasReturnTo && isSaasWorkReturnPath(saasReturnTo)),
    );
    if (
      saasReturnTo
      && isSaasWorkReturnPath(saasReturnTo)
      && ownerAccessToken
      && targetHost === window.location.host
    ) {
      let cancelled = false;
      void (async () => {
        const established = await ensureSaasWorkStorageSession(ownerAccessToken, {
          allowGenericAuthSessionProof: false,
        });
        if (!cancelled && established) {
          rememberRecentWorkSessionMarker();
          clearPendingWorkGoogleBridgePhoneSelection();
          clearPendingWorkGoogleBridgeCallbackTransaction();
          clearPendingSaasHandoffIntent();
          clearPendingSaasHandoffBootstrap();
          window.location.replace(saasReturnTo);
        }
      })();

      return () => {
        cancelled = true;
      };
    }
    if (
      authState === 'authorizationStateReady'
      && (
        isWorkGoogleBridgeCallback(window.location.search, window.location.pathname, window.location.hostname)
        || Boolean(readPendingWorkGoogleBridgeCallbackTransactionId())
      )
    ) {
      let cancelled = false;
      void (async () => {
        const callbackBridgeTransactionId = readWorkGoogleBridgeCallbackTransactionId(
          window.location.search,
          window.location.pathname,
          window.location.hostname,
        );
        const pendingCallbackBridgeTransactionId = readPendingWorkGoogleBridgeCallbackTransactionId();
        const pendingPhoneBridgeTransactionId = readPendingWorkGoogleBridgePhoneSelectionTransactionId();
        const bridgeTransactionId = callbackBridgeTransactionId || pendingCallbackBridgeTransactionId;
        if (!bridgeTransactionId) {
          clearPendingWorkGoogleBridgePhoneSelection();
          clearPendingWorkGoogleBridgeCallbackTransaction();
          return;
        }
        if (pendingPhoneBridgeTransactionId && pendingPhoneBridgeTransactionId === bridgeTransactionId) {
          const targetHostForFallback = readSaasTargetHost();
          if (
            targetHostForFallback
            && targetHostForFallback !== window.location.host
          ) {
            const targetTenantIdForFallback = readSaasTargetTenantId();
            const entries = targetTenantIdForFallback
              ? await fetchWorkAccountBridgeEntries(targetTenantIdForFallback)
              : [];
            if (!cancelled && entries.length > 0) {
              const phoneEntry = currentUserId
                ? entries.find((entry) => entry.userId === currentUserId)
                : undefined;
              if (phoneEntry?.ownerAccessToken) {
                const tokenUrl = new URL('/webchat/', `https://${targetHostForFallback}`);
                tokenUrl.searchParams.set('run_source', 'saas');
                tokenUrl.searchParams.set('return_to', '/work');
                tokenUrl.searchParams.set('ownerAccessToken', phoneEntry.ownerAccessToken);
                clearPendingWorkGoogleBridgePhoneSelection();
                clearPendingWorkGoogleBridgeCallbackTransaction();
                window.location.replace(tokenUrl.toString());
                return;
              }
              const targetUrl = new URL('/work/', `https://${targetHostForFallback}`);
              targetUrl.searchParams.set('return_to', '/work');
              targetUrl.searchParams.set('work_account_bridge', encodeWorkAccountBridgePayload(entries));
              clearPendingWorkGoogleBridgePhoneSelection();
              clearPendingWorkGoogleBridgeCallbackTransaction();
              window.location.replace(targetUrl.toString());
              return;
            }
          }
        }
        const authSessionProof = await waitForWorkGoogleBridgeAuthSessionProof();
        if (cancelled || !authSessionProof) {
          return;
        }
        const response = await fetch('/api/run/saas-bootstrap/work-google-bridge-transaction/consume', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            bridgeTransactionId,
            ...(authSessionProof ? { authSessionProof } : {}),
          }),
        });
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          if (
            [403, 404, 409, 410].includes(response.status)
            && pendingPhoneBridgeTransactionId
            && pendingPhoneBridgeTransactionId === bridgeTransactionId
          ) {
            const targetHostForFallback = readSaasTargetHost();
            if (
              targetHostForFallback
              && targetHostForFallback !== window.location.host
            ) {
              const targetTenantIdForFallback = readSaasTargetTenantId();
              const entries = targetTenantIdForFallback
                ? await fetchWorkAccountBridgeEntries(targetTenantIdForFallback)
                : [];
              if (!cancelled && entries.length > 0) {
                const phoneEntry = currentUserId
                  ? entries.find((entry) => entry.userId === currentUserId)
                  : undefined;
                if (phoneEntry?.ownerAccessToken) {
                  const tokenUrl = new URL('/webchat/', `https://${targetHostForFallback}`);
                  tokenUrl.searchParams.set('run_source', 'saas');
                  tokenUrl.searchParams.set('return_to', '/work');
                  tokenUrl.searchParams.set('ownerAccessToken', phoneEntry.ownerAccessToken);
                  clearPendingWorkGoogleBridgePhoneSelection();
                  clearPendingWorkGoogleBridgeCallbackTransaction();
                  window.location.replace(tokenUrl.toString());
                  return;
                }
                const targetUrl = new URL('/work/', `https://${targetHostForFallback}`);
                targetUrl.searchParams.set('return_to', '/work');
                targetUrl.searchParams.set('work_account_bridge', encodeWorkAccountBridgePayload(entries));
                clearPendingWorkGoogleBridgePhoneSelection();
                clearPendingWorkGoogleBridgeCallbackTransaction();
                window.location.replace(targetUrl.toString());
                return;
              }
            }
          }
          if ([403, 404, 409, 410].includes(response.status)) {
            clearPendingWorkGoogleBridgePhoneSelection();
            clearPendingWorkGoogleBridgeCallbackTransaction();
          }
          return;
        }
        const payload = await response.json();
        const transaction = readCanonicalWorkGoogleBridgeTransactionTruth(payload, bridgeTransactionId);
        const ownerAccessTokenFromConsume = typeof payload?.ownerAccessToken === 'string'
          ? payload.ownerAccessToken
          : undefined;
        const targetUrl = ownerAccessTokenFromConsume && transaction
          ? buildWorkGoogleBridgeSaasReturnUrl({
            ownerAccessToken: ownerAccessTokenFromConsume,
            transaction,
          })
          : undefined;
        clearPendingWorkGoogleBridgePhoneSelection();
        clearPendingWorkGoogleBridgeCallbackTransaction();
        if (!targetUrl) {
          return;
        }
        window.location.replace(targetUrl);
      })();

      return () => {
        cancelled = true;
      };
    }
    if (
      authState === 'authorizationStateReady'
      && workBridgeReturnTo
      && isSaasWorkReturnPath(workBridgeReturnTo)
      && targetHost
      && targetHost !== window.location.host
    ) {
      let cancelled = false;
      void (async () => {
        const targetTenantId = readSaasTargetTenantId();
        if (!targetTenantId) {
          clearPendingSaasHandoffBootstrap();
          return;
        }
        const entries = await fetchWorkAccountBridgeEntriesWithRetry(targetTenantId);
        if (cancelled || entries.length === 0) return;
        const targetUrl = new URL(workBridgeReturnTo, `https://${targetHost}`);
        targetUrl.searchParams.set('work_account_bridge', encodeWorkAccountBridgePayload(entries));
        clearPendingSaasHandoffBootstrap();
        window.location.replace(targetUrl.toString());
      })();

      return () => {
        cancelled = true;
      };
    }
    if (
      authState === 'authorizationStateReady'
      && saasReturnTo
      && isSaasWorkReturnPath(saasReturnTo)
      && targetHost
      && targetHost !== window.location.host
    ) {
      let cancelled = false;
      void (async () => {
        const resolvedOwnerAccessToken = await fetchSaasOwnerAccessToken();
        if (cancelled) {
          return;
        }

        if (!resolvedOwnerAccessToken) {
          const targetTenantId = readSaasTargetTenantId();
          if (!targetTenantId) {
            return;
          }
          const entries = await fetchWorkAccountBridgeEntries(targetTenantId);
          if (cancelled || entries.length === 0) {
            return;
          }
          if (readPendingWorkGoogleBridgePhoneSelectionTransactionId()) {
            const phoneEntry = entries.find((entry) => entry.userId === currentUserId);
            if (phoneEntry?.ownerAccessToken) {
              clearPendingWorkGoogleBridgePhoneSelection();
              const tokenUrl = new URL('/webchat/', `https://${targetHost}`);
              tokenUrl.searchParams.set('run_source', 'saas');
              tokenUrl.searchParams.set('return_to', saasReturnTo);
              tokenUrl.searchParams.set('ownerAccessToken', phoneEntry.ownerAccessToken);
              window.location.replace(tokenUrl.toString());
              return;
            }
            clearPendingWorkGoogleBridgePhoneSelection();
          }
          const targetUrl = new URL(saasReturnTo, `https://${targetHost}`);
          targetUrl.searchParams.set('work_account_bridge', encodeWorkAccountBridgePayload(entries));
          window.location.replace(targetUrl.toString());
          return;
        }

        const targetUrl = new URL('/webchat/', `https://${targetHost}`);
        targetUrl.searchParams.set('run_source', 'saas');
        targetUrl.searchParams.set('return_to', saasReturnTo);
        targetUrl.searchParams.set('ownerAccessToken', resolvedOwnerAccessToken);
        window.location.replace(targetUrl.toString());
      })();

      return () => {
        cancelled = true;
      };
    }

    if (
      authState === 'authorizationStateReady'
      && saasReturnTo
      && isSaasWorkReturnPath(saasReturnTo)
      && ownerAccessToken
    ) {
      let cancelled = false;
      void (async () => {
        const established = await ensureSaasWorkStorageSession(ownerAccessToken, {
          allowGenericAuthSessionProof: false,
        });
        if (!cancelled && established) {
          clearPendingWorkGoogleBridgePhoneSelection();
          clearPendingSaasHandoffIntent();
          clearPendingSaasHandoffBootstrap();
          window.location.replace(saasReturnTo);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    // Saas return-back: when the user signed in via /webchat/?run_source=saas&return_to=<path>,
    // bounce them to the originating page once we hit `authorizationStateReady`.
    // Only same-origin paths (validated by readSafeSaasReturnTo) — never an open redirect.
    if (authState === 'authorizationStateReady') {
      if (saasReturnTo) {
        if (isSaasWorkReturnPath(saasReturnTo)) {
          let cancelled = false;
          void (async () => {
            const established = await ensureSaasWorkStorageSession(ownerAccessToken, {
              allowGenericAuthSessionProof: false,
            });
            if (!cancelled && established) {
              rememberRecentWorkSessionMarker();
              clearPendingWorkGoogleBridgePhoneSelection();
              clearPendingWorkGoogleBridgeCallbackTransaction();
              clearPendingSaasHandoffIntent();
              clearPendingSaasHandoffBootstrap();
              window.location.replace(saasReturnTo);
            }
          })();

          return () => {
            cancelled = true;
          };
        }
        window.location.replace(saasReturnTo);
        return;
      }
    }

    const hasResumeRequest = Boolean(resumeRequest);
    const canOpenResolvedTarget = Boolean(
      currentUserId
      || authState === 'authorizationStateReady',
    );

    const clearResolvedHandoffState = () => {
      clearPendingSaasHandoffIntent();
      clearPendingSaasHandoffBootstrap();

      const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const cleanedUrl = clearSaasHandoffSearchParams(currentUrl);
      if (cleanedUrl !== currentUrl) {
        window.history.replaceState(window.history.state, '', cleanedUrl);
      }
    };

    const cleanupLegacyHandoffArtifacts = (username: string, legacyText?: string) => {
      if (!legacyText) {
        return;
      }

      let attempt = 0;
      const cleanup = () => {
        const global = (window as any).getGlobal?.() as GlobalState | undefined;
        if (!global) {
          if (attempt < SAAS_HANDOFF_MAX_RETRIES) {
            attempt += 1;
            window.setTimeout(cleanup, SAAS_HANDOFF_RETRY_DELAY_MS);
          }
          return;
        }

        const chat = selectChatByUsername(global, username);
        if (!chat) {
          if (attempt < SAAS_HANDOFF_MAX_RETRIES) {
            attempt += 1;
            window.setTimeout(cleanup, SAAS_HANDOFF_RETRY_DELAY_MS);
          }
          return;
        }

        const actions = getActions();
        const currentDraft = selectDraft(global, chat.id, MAIN_THREAD_ID);
        if (currentDraft?.text?.text === legacyText) {
          actions.clearDraft?.({ chatId: chat.id });
        }

        const chatMessages = selectChatMessages(global, chat.id) || {};
        const messageIds = Object.values(chatMessages)
          .filter((message) => {
            const messageText = (message as { text?: { text?: string } }).text?.text
              || message.content?.text?.text;
            return message.isOutgoing && messageText === legacyText;
          })
          .map((message) => message.id);

        if (messageIds.length) {
          actions.deleteMessages?.({
            messageIds,
            shouldDeleteForAll: true,
          });
        }

        if (attempt < SAAS_HANDOFF_MAX_RETRIES) {
          attempt += 1;
          window.setTimeout(cleanup, SAAS_HANDOFF_RETRY_DELAY_MS);
        }
      };

      cleanup();
    };

    const attemptBootstrapOpen = () => {
      if (hasPendingWorkReturn) {
        return false;
      }
      const handoff = buildSaasHandoffBootstrap({ currentUserId });
      if (!handoff) {
        return false;
      }

      const action = getActions().openChatByUsername;
      if (typeof action !== 'function') {
        // eslint-disable-next-line no-console
        console.warn('[run-saas-handoff] bootstrap_open_deferred', {
          username: handoff.username,
          message: 'openChatByUsername unavailable',
        });
        return false;
      }

      try {
        action({
          username: handoff.username,
        });
        rememberSaasMetaCapiTarget({
          username: handoff.username,
        });
        cleanupLegacyHandoffArtifacts(
          handoff.username,
          buildLegacySaasHandoffText({}),
        );

        clearResolvedHandoffState();
        return true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[run-saas-handoff] bootstrap_open_failed', {
          username: handoff.username,
          message: err instanceof Error ? err.message : String(err),
        });
        return false;
      }
    };

    if (!hasResumeRequest && attemptBootstrapOpen()) {
      return;
    }

    let retryCount = 0;
    let retryTimer: number | undefined;
    let cancelled = false;

    const attemptOpen = () => {
      if (!hasResumeRequest) {
        return attemptBootstrapOpen();
      }

      const { clearDraft, openChat, openChatByUsername } = getActions();
      return attemptResolvedSaasHandoffOpen({
        canOpenResolvedTarget,
        currentUserId,
        clearResolvedHandoffState,
        clearDraft,
        openChat,
        openChatByUsername,
        onResolvedTargetOpen: ({ chatId, username }) => {
          rememberSaasMetaCapiTarget({ chatId, username });
        },
        hasResolvedTargetEntity: (chatId) => {
          const global = (window as any).getGlobal?.() as GlobalState | undefined;
          if (!global) {
            return true;
          }

          const chat = typeof selectChat === 'function' ? selectChat(global, chatId) : undefined;
          const user = typeof selectUser === 'function' ? selectUser(global, chatId) : undefined;
          return Boolean(chat && user);
        },
      });
    };

    const scheduleRetry = () => {
      retryTimer = window.setTimeout(() => {
        const result = attemptOpen();
        if (typeof result === 'boolean') {
          if (cancelled || result) {
            return;
          }

          retryCount += 1;
          if (retryCount < SAAS_HANDOFF_MAX_RETRIES) {
            scheduleRetry();
          }
          return;
        }

        void result.then((opened) => {
          if (cancelled || opened) {
            return;
          }

          retryCount += 1;
          if (retryCount < SAAS_HANDOFF_MAX_RETRIES) {
            scheduleRetry();
          }
        });
      }, SAAS_HANDOFF_RETRY_DELAY_MS);
    };

    const initialResult = attemptOpen();
    if (typeof initialResult === 'boolean') {
      if (!initialResult) {
        scheduleRetry();
      }
    } else {
      void initialResult.then((opened) => {
        if (!opened) {
          scheduleRetry();
        }
      });
    }

    return () => {
      cancelled = true;
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [authState, currentUserId]);

  useEffect(() => {
    if (IS_INSTALL_PROMPT_SUPPORTED) {
      setupBeforeInstallPrompt();
    }
  }, []);

  useEffect(() => {
    const hash = getInitialLocationHash();
    // If there is no stored session on first slot, navigate to any other slot with stored session
    if (!hasStoredSession() && !ACCOUNT_SLOT && !hash) {
      const accounts = getAccountsInfo();
      Object.keys(accounts)
        .map(Number)
        .sort((a, b) => b - a)
        .forEach((key) => {
          const slot = Number(key);
          const account = accounts[slot];
          if (account) {
            const url = getAccountSlotUrl(slot);
            window.location.href = `${url}#${hash || 'login'}`;
          }
        });
    }

    // TODO[Passcode]: Remove when multiacc passcode is implemented
    const checkMultiaccPasscode = async () => {
      if (checkSessionLocked() && ACCOUNT_SLOT && await hasEncryptedSession()) {
        const url = getAccountSlotUrl(1);
        window.location.href = url;
      }
    };
    checkMultiaccPasscode();
  }, []);

  // Prevent drop on elements that do not accept it
  useEffect(() => {
    const body = document.body;
    const handleDrag = (e: DragEvent) => {
      e.preventDefault();
      if (!e.dataTransfer) return;
      if (!(e.target as HTMLElement).dataset.dropzone) {
        e.dataTransfer.dropEffect = 'none';
      } else {
        e.dataTransfer.dropEffect = 'copy';
      }
    };
    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
    };
    body.addEventListener('drop', handleDrop);
    body.addEventListener('dragover', handleDrag);
    body.addEventListener('dragenter', handleDrag);

    return () => {
      body.removeEventListener('drop', handleDrop);
      body.removeEventListener('dragover', handleDrag);
      body.removeEventListener('dragenter', handleDrag);
    };
  }, []);

  // return <Test />;

  let activeKey: AppScreens = AppScreens.auth;
  let page: UiLoaderPage | undefined;
  const hasResolvedSaasResumeHint = Boolean(buildSaasHandoffResumeRequest({ currentUserId }));
  const hasBootstrapResumeHint = Boolean(parseInitialLocationHash()?.tgWebAuthToken);
  const canEnterMainForSaasResume = Boolean(
    currentUserId
    || authState === 'authorizationStateReady',
  );
  const shouldIgnoreStoredSessionForSaasResume = hasResolvedSaasResumeHint && !canEnterMainForSaasResume;

  if (inactiveReason) {
    activeKey = AppScreens.inactive;
  } else if (isScreenLocked) {
    page = 'lock';
    activeKey = AppScreens.lock;
  } else if (authState) {
    switch (authState) {
      case 'authorizationStateWaitPhoneNumber':
        page = 'authPhoneNumber';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitCode':
        page = 'authCode';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitPassword':
        page = 'authPassword';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitRegistration':
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitQrCode':
        page = 'authQrCode';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateClosed':
      case 'authorizationStateClosing':
      case 'authorizationStateLoggingOut':
      case 'authorizationStateReady':
        page = 'main';
        activeKey = AppScreens.main;
        break;
    }
  } else if (hasStoredSession() && !shouldIgnoreStoredSessionForSaasResume) {
    page = 'main';
    activeKey = AppScreens.main;
  } else if (hasPasscode) {
    activeKey = AppScreens.lock;
  } else {
    page = isMobileOs || shouldPreferPhoneAuth(window.location.search) ? 'authPhoneNumber' : 'authQrCode';
    activeKey = AppScreens.auth;
  }

  if (activeKey !== AppScreens.lock
    && activeKey !== AppScreens.inactive
    && activeKey !== AppScreens.main
    && (hasBootstrapResumeHint || (hasResolvedSaasResumeHint && canEnterMainForSaasResume))
    && !hasWebAuthTokenFailed) {
    page = 'main';
    activeKey = AppScreens.main;
  }

  useEffect(() => {
    updateSizes();
  }, []);

  useEffect(() => {
    if (inactiveReason) {
      document.title = INACTIVE_PAGE_TITLE;
    } else {
      document.title = ACTIVE_PAGE_TITLE;
    }
  }, [inactiveReason]);

  const prevActiveKey = usePrevious(activeKey);

  function renderContent() {
    switch (activeKey) {
      case AppScreens.auth:
        return <Auth />;
      case AppScreens.main:
        return <Main isMobile={isMobile} />;
      case AppScreens.lock:
        return <LockScreen isLocked={isScreenLocked} />;
      case AppScreens.inactive:
        return <AppInactive inactiveReason={inactiveReason!} />;
    }
  }

  useTauriDrag();

  useLayoutEffect(() => {
    document.body.classList.add(styles.bg);
  }, []);

  useLayoutEffect(() => {
    document.body.style.setProperty(
      '--theme-background-color',
      theme === 'dark' ? DARK_THEME_BG_COLOR : LIGHT_THEME_BG_COLOR,
    );
  }, [theme]);

  useLayoutEffect(() => {
    if (actionMessageBg) {
      document.body.style.setProperty('--action-message-bg', actionMessageBg);
    }
  }, [actionMessageBg]);

  const getIsInBackgroundLocal = getIsInBackground;
  useSignalEffect(() => {
    // Mutation forced to avoid RAF throttling in background
    forceMutation(() => {
      document.body.classList.toggle('in-background', getIsInBackgroundLocal());
    }, document.body);
  }, [getIsInBackgroundLocal]);

  function handleTermsAgree() {
    acceptRunTermsOfService();
  }

  function handleTermsDecline() {
    signOut({ forceInitApi: true });
  }

  return (
    <UiLoader page={page} isMobile={isMobile}>
      <Transition
        name="fade"
        activeKey={activeKey}
        shouldCleanup
        className={buildClassName(
          'full-height',
          (activeKey === AppScreens.auth || prevActiveKey === AppScreens.auth) && 'is-auth',
        )}
        renderCount={TRANSITION_RENDER_COUNT}
      >
        {renderContent}
      </Transition>
      {activeKey === AppScreens.auth && isTestServer && <div className="test-server-badge">Test server</div>}
      <Notifications />
      <TermsModal
        isOpen={isRunTermsNeeded}
        onAgree={handleTermsAgree}
        onDecline={handleTermsDecline}
      />
    </UiLoader>
  );
};

export default withGlobal(
  (global): Complete<StateProps> => {
    const { state: authState, hasWebAuthTokenFailed, hasWebAuthTokenPasswordRequired } = global.auth;
    return {
      authState,
      connectionState: global.connectionState,
      currentUserId: global.currentUserId,
      isScreenLocked: global.passcode?.isScreenLocked,
      hasPasscode: global.passcode?.hasPasscode,
      inactiveReason: selectTabState(global).inactiveReason,
      hasWebAuthTokenFailed: hasWebAuthTokenFailed || hasWebAuthTokenPasswordRequired,
      theme: selectTheme(global),
      isTestServer: global.config?.isTestServer,
      actionMessageBg: selectActionMessageBg(global),
      isRunTermsNeeded: Boolean(global.runTermsOfService),
    };
  },
)(App);
